import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';

// Centralises per-page SEO tags so any component can give search engines and
// social crawlers a real title/description/image instead of the app-wide
// defaults. This is a client-side helper: it fills the tags once Angular runs,
// which helps crawlers that execute JavaScript. Full coverage still needs SSR
// or prerendering (the HTML shipped from the server is otherwise just
// <app-root>), but this is the high-value first step.
@Injectable({
  providedIn: 'root'
})
export class SeoService {

  private readonly siteName = 'أكاديمية الفنون';

  constructor(private title: Title, private meta: Meta) { }

  // Sets the document title plus the description/canonical and the Open Graph
  // and Twitter tags used when a page is shared. Any missing field falls back
  // to a sensible default so we never emit empty tags.
  update(opts: { title?: string, description?: string, image?: string, url?: string, type?: string } = {}) {
    const pageTitle = opts.title
      ? `${opts.title} | ${this.siteName}`
      : this.siteName;
    const description = this.clean(opts.description) || this.siteName;
    const url = opts.url || (typeof window !== 'undefined' ? window.location.href : '');

    this.title.setTitle(pageTitle);

    this.setName('description', description);
    this.setProperty('og:title', pageTitle);
    this.setProperty('og:description', description);
    this.setProperty('og:type', opts.type || 'article');
    this.setProperty('og:site_name', this.siteName);
    if (url) { this.setProperty('og:url', url); }
    if (opts.image) { this.setProperty('og:image', opts.image); }

    this.setName('twitter:card', opts.image ? 'summary_large_image' : 'summary');
    this.setName('twitter:title', pageTitle);
    this.setName('twitter:description', description);
    if (opts.image) { this.setName('twitter:image', opts.image); }

    this.setCanonical(url);

    // Readiness signal for the Dynamic Rendering service: update() runs once a
    // page's real data has loaded (title/description are known), so this is the
    // precise moment a crawler snapshot should be taken. The prerender headless
    // browser waits for this flag before capturing the HTML.
    if (typeof window !== 'undefined') { (window as any).prerenderReady = true; }
  }

  // Convenience wrapper: pulls title/description/image straight off an API
  // entity (news, event, research, …) reading each from a list of candidate
  // keys, since the backend's field casing isn't consistent. Callers can pass
  // their own key lists or fall back to the common defaults.
  updateFrom(
    data: any,
    keys: { title?: string[], description?: string[], image?: string[] } = {}
  ) {
    if (!data) { this.update(); return; }
    const title = this.pick(data, keys.title || ['TITLE', 'title', 'NEW_TITLE', 'Name']);
    const description = this.pick(
      data,
      keys.description || ['DETAILS', 'details', 'DESCRIPTION', 'ABOUT', 'SUMMARY']
    );
    const image = this.pick(data, keys.image || ['IMG_PATH', 'IMG', 'IMAGE', 'IMAGE_PATH']);
    this.update({ title, description, image });
  }

  // Returns the first non-empty value among the given keys.
  private pick(obj: any, keys: string[]): string {
    if (!obj) { return ''; }
    for (const k of keys) {
      if (obj[k] != null && String(obj[k]).trim() !== '') { return String(obj[k]).trim(); }
    }
    return '';
  }

  // Strips HTML tags and collapses whitespace, then trims to a meta-friendly
  // length. News DETAILS comes back as an HTML blob, so this keeps the
  // description readable and under the ~160 char cutoff crawlers show.
  private clean(html?: string): string {
    if (!html) { return ''; }
    const text = String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return text.length > 160 ? text.slice(0, 157).replace(/\s+$/, '') + '…' : text;
  }

  private setName(name: string, content: string) {
    this.meta.updateTag({ name, content });
  }

  private setProperty(property: string, content: string) {
    this.meta.updateTag({ property, content });
  }

  // Keeps a single <link rel="canonical"> in <head> pointing at the current URL,
  // so the multi-subdomain setup doesn't read as duplicate content.
  private setCanonical(url: string) {
    if (!url || typeof document === 'undefined') { return; }
    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }
}
